import { supabase } from '../supabase';
import { fetchAllRows } from '../utils/async/fetchAllRows';

/**
 * Extracted from estimateService.js (500-line cap) — `getEstimates` /
 * `getArchivedEstimates` moved here verbatim when adding the fetchAllRows
 * unpaged path pushed the parent file over budget. Re-exported from
 * estimateService.js so existing `import { getEstimates } from
 * './estimateService'` call sites keep working. Pure move — no behavior
 * change beyond what estimateService.js's own history already carries.
 *
 * Every estimate read in this file selects `*`, so SERVER-DERIVED columns —
 * notably `fully_paid_at` (20260806120000) — reach the client for free.
 * If you ever narrow one of these selects to an explicit column list, you must
 * carry `fully_paid_at` with it: it is the ONLY signal that a quote was billed
 * in full AND collected in full, and dropping it silently degrades the UI to
 * "not paid" with no error anywhere.
 * It is read-only to the client — see the write note on updateEstimate in
 * estimateService.js.
 * KAN-1118: the payment-plan columns (payment_plan, billed_invoice_count, billed_amount, paid_amount,
 * earliest_unpaid_due_on, earliest_unpaid_due_sequence) ride the same `*` — the estimate row's progress
 * line reads them, so the same rule holds for any explicit column list (convertEstimateAsyncSteps.js
 * ESTIMATE_PROGRESS_COLUMNS is the one re-read of them).
 */
const ESTIMATE_COLUMNS = `*, customers (full_name, email, cc_emails, comm_opt_out, service_address)`;

export const getEstimates = async (tenantId, { year, page, pageSize = 50 } = {}) => {
  if (!tenantId) return { data: null, error: new Error('tenantId is required') };
  const applyFilters = (q) => {
    let query = q.eq('tenant_id', tenantId).is('archived_at', null);
    if (year) {
      query = query.gte('created_at', `${year}-01-01T00:00:00`).lte('created_at', `${year}-12-31T23:59:59`);
    }
    return query;
  };

  // No `page` ⇒ go through fetchAllRows so PostgREST's silent 1000-row cap
  // cannot truncate a tenant-wide/multi-year read. `page` passed ⇒ unchanged.
  if (!page) {
    const res = await fetchAllRows((opts) =>
      applyFilters(supabase.from('estimates').select(ESTIMATE_COLUMNS, opts))
        .order('created_at', { ascending: false })
        .order('id', { ascending: true }),
    );
    return { data: res.data, error: res.error, count: res.count };
  }

  // bounded: paged path — the caller passes page/pageSize (`.range()` below).
  let query = applyFilters(supabase.from('estimates').select(ESTIMATE_COLUMNS, { count: 'exact' }));
  const from = (page - 1) * pageSize;
  query = query.range(from, from + pageSize - 1);
  const { data, error, count } = await query.order('created_at', { ascending: false });
  return { data, error, count };
};

/** Estimates with archived_at set (soft-deleted). Same shape/options as getEstimates. */
export const getArchivedEstimates = async (tenantId, { year, page, pageSize = 50 } = {}) => {
  if (!tenantId) return { data: null, error: new Error('tenantId is required') };
  const applyFilters = (q) => {
    let query = q.eq('tenant_id', tenantId).not('archived_at', 'is', null);
    if (year) {
      query = query.gte('created_at', `${year}-01-01T00:00:00`).lte('created_at', `${year}-12-31T23:59:59`);
    }
    return query;
  };

  if (!page) {
    const res = await fetchAllRows((opts) =>
      applyFilters(supabase.from('estimates').select(ESTIMATE_COLUMNS, opts))
        .order('archived_at', { ascending: false })
        .order('id', { ascending: true }),
    );
    return { data: res.data, error: res.error, count: res.count };
  }

  // bounded: paged path — the caller passes page/pageSize (`.range()` below).
  let query = applyFilters(supabase.from('estimates').select(ESTIMATE_COLUMNS, { count: 'exact' }));
  const from = (page - 1) * pageSize;
  query = query.range(from, from + pageSize - 1);
  const { data, error, count } = await query.order('archived_at', { ascending: false });
  return { data, error, count };
};
